/**
 * DiscrepancyList Component
 * 
 * Displays a list of discrepancies with severity indicators, filtering, and resolution status.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';

/**
 * Severity configurations
 */
const SEVERITY_CONFIG = {
  critical: {
    label: 'severity.critical',
    color: '#b91c1c',
    bgColor: '#fef2f2',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>`,
    priority: 4,
  },
  high: {
    label: 'severity.high',
    color: '#dc2626',
    bgColor: '#fee2e2',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
    priority: 3,
  },
  medium: {
    label: 'severity.medium',
    color: '#d97706',
    bgColor: '#fef3c7',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>`,
    priority: 2,
  },
  low: {
    label: 'severity.low',
    color: '#2563eb',
    bgColor: '#dbeafe',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>`,
    priority: 1,
  },
  info: {
    label: 'severity.info',
    color: '#6b7280',
    bgColor: '#f3f4f6',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>`,
    priority: 0,
  },
};

/**
 * Resolution status configurations
 */
const RESOLUTION_STATUS_CONFIG = {
  pending: {
    label: 'discrepancy.pending',
    color: '#9333ea',
    bgColor: '#f3e8ff',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`,
  },
  under_review: {
    label: 'discrepancy.underReview',
    color: '#2563eb',
    bgColor: '#dbeafe',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>`,
  },
  resolved: {
    label: 'discrepancy.resolved',
    color: '#059669',
    bgColor: '#d1fae5',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>`,
  },
  dismissed: {
    label: 'discrepancy.dismissed',
    color: '#6b7280',
    bgColor: '#f3f4f6',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
    </svg>`,
  },
};

/**
 * Discrepancy type configurations
 */
const DISCREPANCY_TYPE_CONFIG = {
  // Display categories
  pricing: {
    label: 'discrepancy.type.pricing',
    icon: '💰',
    description: 'discrepancy.type.pricingDesc',
  },
  quantity: {
    label: 'discrepancy.type.quantity',
    icon: '📦',
    description: 'discrepancy.type.quantityDesc',
  },
  terms: {
    label: 'discrepancy.type.terms',
    icon: '📋',
    description: 'discrepancy.type.termsDesc',
  },
  dates: {
    label: 'discrepancy.type.dates',
    icon: '📅',
    description: 'discrepancy.type.datesDesc',
  },
  missing_item: {
    label: 'discrepancy.type.missingItem',
    icon: '❓',
    description: 'discrepancy.type.missingItemDesc',
  },
  unauthorized: {
    label: 'discrepancy.type.unauthorized',
    icon: '🚫',
    description: 'discrepancy.type.unauthorizedDesc',
  },
  duplicate: {
    label: 'discrepancy.type.duplicate',
    icon: '♻️',
    description: 'discrepancy.type.duplicateDesc',
  },
  other: {
    label: 'discrepancy.type.other',
    icon: '📝',
    description: 'discrepancy.type.otherDesc',
  },
  // Backend type mappings (snake_case from API)
  price_overcharge: {
    label: 'discrepancy.type.pricing',
    icon: '💰',
    description: 'discrepancy.overchargeExplanation',
  },
  missing_discount: {
    label: 'discrepancy.type.pricing',
    icon: '💰',
    description: 'discrepancy.type.pricingDesc',
  },
  incorrect_tax: {
    label: 'discrepancy.type.pricing',
    icon: '💰',
    description: 'discrepancy.type.pricingDesc',
  },
  duplicate_charge: {
    label: 'discrepancy.type.duplicate',
    icon: '♻️',
    description: 'discrepancy.type.duplicateDesc',
  },
  quantity_excess: {
    label: 'discrepancy.type.quantity',
    icon: '📦',
    description: 'discrepancy.type.quantityDesc',
  },
  unauthorized_item: {
    label: 'discrepancy.type.unauthorized',
    icon: '🚫',
    description: 'discrepancy.type.unauthorizedDesc',
  },
  contract_expired: {
    label: 'discrepancy.type.dates',
    icon: '📅',
    description: 'discrepancy.type.datesDesc',
  },
};

/**
 * Format currency (uses user's preferred currency as default)
 */
function formatCurrency(amount, currency = null) {
  if (amount === null || amount === undefined) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  const currencyCode = currency || getCurrency();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffMinutes < 1) return t('time.justNow') || 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} ${t('time.minutesAgo') || 'min ago'}`;
  if (diffHours < 24) return `${diffHours} ${t('time.hoursAgo') || 'hours ago'}`;
  if (diffDays < 7) return `${diffDays} ${t('time.daysAgo') || 'days ago'}`;
  
  return formatDate(dateStr);
}

/**
 * Render severity badge
 */
function renderSeverityBadge(severity) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  return `
    <span class="severity-badge severity-${severity}" style="color: ${config.color}; background: ${config.bgColor}">
      ${config.icon}
      <span>${t(config.label) || severity}</span>
    </span>
  `;
}

/**
 * Render resolution status badge
 */
function renderResolutionBadge(status) {
  const config = RESOLUTION_STATUS_CONFIG[status] || RESOLUTION_STATUS_CONFIG.pending;
  return `
    <span class="resolution-badge resolution-${status}" style="color: ${config.color}; background: ${config.bgColor}">
      ${config.icon}
      <span>${t(config.label) || status}</span>
    </span>
  `;
}

/**
 * Render discrepancy type badge
 */
function renderTypeBadge(type) {
  const config = DISCREPANCY_TYPE_CONFIG[type] || DISCREPANCY_TYPE_CONFIG.other;
  return `
    <span class="type-badge" title="${t(config.description) || ''}">
      <span class="type-icon">${config.icon}</span>
      <span class="type-label">${t(config.label) || type}</span>
    </span>
  `;
}

/**
 * Render financial impact indicator
 */
function renderFinancialImpact(impact, currency = null) {
  if (!impact) return '';

  const isPositive = impact > 0;
  const impactClass = isPositive ? 'impact-overcharge' : 'impact-undercharge';
  const impactLabel = isPositive
    ? (t('discrepancy.overcharge') || 'Overcharge')
    : (t('discrepancy.undercharge') || 'Undercharge');

  return `
    <div class="financial-impact ${impactClass}">
      <span class="impact-label">${impactLabel}:</span>
      <span class="impact-value">${formatCurrency(Math.abs(impact), currency)}</span>
    </div>
  `;
}

/**
 * Render a single discrepancy card
 */
function renderDiscrepancyCard(discrepancy) {
  const config = SEVERITY_CONFIG[discrepancy.severity] || SEVERITY_CONFIG.info;
  
  return `
    <div class="discrepancy-card severity-${discrepancy.severity}" data-discrepancy-id="${discrepancy.id}">
      <div class="discrepancy-card-header">
        <div class="discrepancy-type-severity">
          ${renderTypeBadge(discrepancy.discrepancyType)}
          ${renderSeverityBadge(discrepancy.severity)}
        </div>
        ${renderResolutionBadge(discrepancy.resolutionStatus)}
      </div>
      
      <div class="discrepancy-card-body">
        <h3 class="discrepancy-title">
          <a href="#/discrepancies/${discrepancy.id}">${discrepancy.description}</a>
        </h3>
        
        ${renderFinancialImpact(discrepancy.financialImpact, discrepancy.currency)}
        
        <div class="discrepancy-meta">
          <div class="meta-item">
            <span class="meta-label">${t('discrepancy.invoice') || 'Invoice'}:</span>
            <a href="#/invoices/${discrepancy.invoiceId}" class="meta-link">${discrepancy.invoiceNumber || discrepancy.invoiceId}</a>
          </div>
          <div class="meta-item">
            <span class="meta-label">${t('discrepancy.contract') || 'Contract'}:</span>
            <a href="#/contracts/${discrepancy.contractId}" class="meta-link">${discrepancy.contractTitle || discrepancy.contractNumber || discrepancy.contractId}</a>
          </div>
        </div>
        
        ${discrepancy.aiExplanation ? `
          <div class="ai-explanation">
            <span class="ai-badge">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              ${t('analysis.poweredByAI') || 'Powered by AI'}
            </span>
            <p>${discrepancy.aiExplanation}</p>
          </div>
        ` : ''}
      </div>
      
      <div class="discrepancy-card-footer">
        <span class="discrepancy-time">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          ${formatRelativeTime(discrepancy.detectedAt)}
        </span>
        <div class="discrepancy-actions">
          <button class="btn-icon" onclick="window.viewDiscrepancy('${discrepancy.id}')" title="${t('common.view') || 'View'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          ${discrepancy.resolutionStatus !== 'resolved' ? `
            <button class="btn-icon" onclick="window.resolveDiscrepancy('${discrepancy.id}')" title="${t('discrepancy.resolve') || 'Resolve'}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </button>
          ` : ''}
        </div>
      </div>
    </div>
  `;
}

/**
 * Render a single discrepancy row (table view)
 */
function renderDiscrepancyRow(discrepancy) {
  return `
    <tr class="discrepancy-row severity-${discrepancy.severity}" data-discrepancy-id="${discrepancy.id}">
      <td class="cell-checkbox">
        <input type="checkbox" class="discrepancy-checkbox" value="${discrepancy.id}" />
      </td>
      <td class="cell-type">${renderTypeBadge(discrepancy.discrepancyType)}</td>
      <td class="cell-description">
        <div class="description-cell">
          <a href="#/discrepancies/${discrepancy.id}" class="discrepancy-link">${discrepancy.description}</a>
          <span class="sub-info">${discrepancy.invoiceNumber || discrepancy.invoiceId}</span>
        </div>
      </td>
      <td class="cell-severity">${renderSeverityBadge(discrepancy.severity)}</td>
      <td class="cell-impact">
        ${discrepancy.financialImpact ? formatCurrency(discrepancy.financialImpact, discrepancy.currency) : '-'}
      </td>
      <td class="cell-status">${renderResolutionBadge(discrepancy.resolutionStatus)}</td>
      <td class="cell-date">${formatRelativeTime(discrepancy.detectedAt)}</td>
      <td class="cell-actions">
        <div class="action-buttons">
          <button class="btn-icon" onclick="window.viewDiscrepancy('${discrepancy.id}')" title="${t('common.view') || 'View'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      </td>
    </tr>
  `;
}

/**
 * Render discrepancy filters
 */
function renderFilters(activeFilters = {}) {
  return `
    <div class="discrepancy-filters">
      <div class="filter-group">
        <label>${t('discrepancy.severity') || 'Severity'}</label>
        <select id="filter-severity" class="filter-select" onchange="window.applyDiscrepancyFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="critical" ${activeFilters.severity === 'critical' ? 'selected' : ''}>${t('severity.critical') || 'Critical'}</option>
          <option value="high" ${activeFilters.severity === 'high' ? 'selected' : ''}>${t('severity.high') || 'High'}</option>
          <option value="medium" ${activeFilters.severity === 'medium' ? 'selected' : ''}>${t('severity.medium') || 'Medium'}</option>
          <option value="low" ${activeFilters.severity === 'low' ? 'selected' : ''}>${t('severity.low') || 'Low'}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>${t('common.type') || 'Type'}</label>
        <select id="filter-type" class="filter-select" onchange="window.applyDiscrepancyFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="price_overcharge" ${activeFilters.type === 'price_overcharge' ? 'selected' : ''}>${t('discrepancy.type.pricing') || 'Price Overcharge'}</option>
          <option value="missing_discount" ${activeFilters.type === 'missing_discount' ? 'selected' : ''}>${t('discrepancy.type.missingDiscount') || 'Missing Discount'}</option>
          <option value="quantity_excess" ${activeFilters.type === 'quantity_excess' ? 'selected' : ''}>${t('discrepancy.type.quantity') || 'Quantity Excess'}</option>
          <option value="duplicate_charge" ${activeFilters.type === 'duplicate_charge' ? 'selected' : ''}>${t('discrepancy.type.duplicate') || 'Duplicate Charge'}</option>
          <option value="unauthorized_item" ${activeFilters.type === 'unauthorized_item' ? 'selected' : ''}>${t('discrepancy.type.unauthorized') || 'Unauthorized Item'}</option>
          <option value="incorrect_tax" ${activeFilters.type === 'incorrect_tax' ? 'selected' : ''}>${t('discrepancy.type.incorrectTax') || 'Incorrect Tax'}</option>
          <option value="contract_expired" ${activeFilters.type === 'contract_expired' ? 'selected' : ''}>${t('discrepancy.type.dates') || 'Contract Expired'}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>${t('discrepancy.status') || 'Status'}</label>
        <select id="filter-status" class="filter-select" onchange="window.applyDiscrepancyFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="pending" ${activeFilters.status === 'pending' ? 'selected' : ''}>${t('discrepancy.pending') || 'Pending'}</option>
          <option value="under_review" ${activeFilters.status === 'under_review' ? 'selected' : ''}>${t('discrepancy.underReview') || 'Under Review'}</option>
          <option value="resolved" ${activeFilters.status === 'resolved' ? 'selected' : ''}>${t('discrepancy.resolved') || 'Resolved'}</option>
          <option value="dismissed" ${activeFilters.status === 'dismissed' ? 'selected' : ''}>${t('discrepancy.dismissed') || 'Dismissed'}</option>
        </select>
      </div>
      <div class="filter-group filter-actions">
        <button class="btn btn-secondary btn-sm" onclick="window.clearDiscrepancyFilters()">
          ${t('common.clear') || 'Clear'}
        </button>
      </div>
    </div>
  `;
}

/**
 * Render view toggle
 */
function renderViewToggle(activeView = 'card') {
  return `
    <div class="view-toggle">
      <button class="view-btn ${activeView === 'card' ? 'active' : ''}" onclick="window.setDiscrepancyView('card')" title="${t('common.cardView') || 'Card View'}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      </button>
      <button class="view-btn ${activeView === 'table' ? 'active' : ''}" onclick="window.setDiscrepancyView('table')" title="${t('common.tableView') || 'Table View'}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="8" y1="6" x2="21" y2="6"/>
          <line x1="8" y1="12" x2="21" y2="12"/>
          <line x1="8" y1="18" x2="21" y2="18"/>
          <line x1="3" y1="6" x2="3.01" y2="6"/>
          <line x1="3" y1="12" x2="3.01" y2="12"/>
          <line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
      </button>
    </div>
  `;
}

/**
 * Render empty state
 */
function renderEmptyState() {
  return `
    <div class="empty-state success-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="empty-icon success">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      <h3>${t('discrepancy.noDiscrepancies') || 'No discrepancies found'}</h3>
      <p>${t('discrepancy.noDiscrepanciesDescription') || 'All your invoices match their contracts perfectly!'}</p>
    </div>
  `;
}

/**
 * Render summary stats
 */
function renderSummaryStats(stats) {
  return `
    <div class="discrepancy-stats">
      <div class="stat-card stat-critical">
        <div class="stat-icon">
          ${SEVERITY_CONFIG.critical.icon}
        </div>
        <div class="stat-content">
          <span class="stat-value">${stats.critical || 0}</span>
          <span class="stat-label">${t('severity.critical') || 'Critical'}</span>
        </div>
      </div>
      <div class="stat-card stat-high">
        <div class="stat-icon">
          ${SEVERITY_CONFIG.high.icon}
        </div>
        <div class="stat-content">
          <span class="stat-value">${stats.high || 0}</span>
          <span class="stat-label">${t('severity.high') || 'High'}</span>
        </div>
      </div>
      <div class="stat-card stat-pending">
        <div class="stat-icon">
          ${RESOLUTION_STATUS_CONFIG.pending.icon}
        </div>
        <div class="stat-content">
          <span class="stat-value">${stats.pending || 0}</span>
          <span class="stat-label">${t('discrepancy.pending') || 'Pending'}</span>
        </div>
      </div>
      <div class="stat-card stat-impact">
        <div class="stat-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-value">${formatCurrency(stats.totalImpact || 0)}</span>
          <span class="stat-label">${t('discrepancy.totalImpact') || 'Total Impact'}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render pagination
 */
function renderPagination(pagination) {
  const { page, totalPages, totalItems } = pagination;
  
  if (totalPages <= 1) return '';
  
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return `
    <div class="pagination">
      <span class="pagination-info">
        ${t('common.showing') || 'Showing'} ${(page - 1) * 20 + 1} - ${Math.min(page * 20, totalItems)} ${t('common.of') || 'of'} ${totalItems}
      </span>
      <div class="pagination-buttons">
        <button class="btn-page" ${page === 1 ? 'disabled' : ''} onclick="window.goToDiscrepancyPage(${page - 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        ${pages.map(p => `
          <button class="btn-page ${p === page ? 'active' : ''}" onclick="window.goToDiscrepancyPage(${p})">${p}</button>
        `).join('')}
        <button class="btn-page" ${page === totalPages ? 'disabled' : ''} onclick="window.goToDiscrepancyPage(${page + 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  `;
}

/**
 * Render the full discrepancy list component
 */
export function renderDiscrepancyList(options = {}) {
  const {
    discrepancies = [],
    filters = {},
    pagination = { page: 1, totalPages: 1, totalItems: 0 },
    view = 'card',
    showFilters = true,
    showStats = true,
    stats = {},
  } = options;

  return `
    <div class="discrepancy-list-container">
      <div class="discrepancy-list-header">
        <div class="header-left">
          <h2>${t('nav.discrepancies') || 'Discrepancies'}</h2>
          <span class="item-count">${pagination.totalItems} ${t('common.items') || 'items'}</span>
        </div>
        <div class="header-right">
          ${renderViewToggle(view)}
          <button class="btn btn-secondary" onclick="window.exportDiscrepancies()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            ${t('common.export') || 'Export'}
          </button>
        </div>
      </div>

      ${showStats ? renderSummaryStats(stats) : ''}

      ${showFilters ? renderFilters(filters) : ''}

      ${discrepancies.length === 0 ? renderEmptyState() : (
        view === 'card' ? `
          <div class="discrepancy-grid">
            ${discrepancies.map(d => renderDiscrepancyCard(d)).join('')}
          </div>
        ` : `
          <div class="table-container">
            <table class="discrepancy-table">
              <thead>
                <tr>
                  <th class="th-checkbox">
                    <input type="checkbox" id="select-all-discrepancies" onchange="window.toggleAllDiscrepancies(this.checked)" />
                  </th>
                  <th>${t('discrepancy.type') || 'Type'}</th>
                  <th class="th-sortable" data-sort="description">${t('common.description') || 'Description'}</th>
                  <th class="th-sortable" data-sort="severity">${t('discrepancy.severity') || 'Severity'}</th>
                  <th class="th-sortable" data-sort="financialImpact">${t('discrepancy.impact') || 'Impact'}</th>
                  <th>${t('discrepancy.status') || 'Status'}</th>
                  <th class="th-sortable" data-sort="detectedAt">${t('discrepancy.detected') || 'Detected'}</th>
                  <th>${t('common.actions') || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                ${discrepancies.map(d => renderDiscrepancyRow(d)).join('')}
              </tbody>
            </table>
          </div>
        `
      )}
      
      ${renderPagination(pagination)}
    </div>
  `;
}

/**
 * Initialize discrepancy list event handlers
 */
export function initDiscrepancyListHandlers() {
  window.toggleAllDiscrepancies = (checked) => {
    const checkboxes = document.querySelectorAll('.discrepancy-checkbox');
    checkboxes.forEach(cb => cb.checked = checked);
  };
}

export { SEVERITY_CONFIG, RESOLUTION_STATUS_CONFIG, DISCREPANCY_TYPE_CONFIG };
